import { PlatformTenantBanner } from "@/components/shell/PlatformTenantBanner";
import { useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import "@/i18n";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, PublicOnlyRoute, PortalRoute } from "@/components/auth/ProtectedRoute";
import RequireTenant from "@/components/shell/RequireTenant";
import { Sidebar } from "@/components/shell/Sidebar";
import { Topbar } from "@/components/shell/Topbar";
import { MobileDrawer } from "@/components/shell/MobileDrawer";
import { SubscriptionBanner } from "@/components/shell/SubscriptionBanner";
import { ImpersonationBanner } from "@/components/shell/ImpersonationBanner";
import { useTheme } from "@/hooks/useTheme";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Activate from "@/pages/Activate";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Onboarding from "@/pages/Onboarding";
import Branches from "@/pages/settings/Branches";
import Roles from "@/pages/settings/Roles";
import Staff from "@/pages/settings/Staff";
import Institution from "@/pages/settings/Institution";
import AuditViewer from "@/pages/settings/AuditViewer";
import Rooms from "@/pages/Rooms";
import People from "@/pages/People";
import PersonDetail from "@/pages/PersonDetail";
import Programs from "@/pages/Programs";
import Groups from "@/pages/Groups";
import GroupDetail from "@/pages/GroupDetail";
import Schedule from "@/pages/Schedule";
import Invoices from "@/pages/finance/Invoices";
import Payments from "@/pages/finance/Payments";
import DebtPage from "@/pages/finance/Debt";
import Cash from "@/pages/finance/Cash";
import OtherIncome from "@/pages/finance/OtherIncome";
import NotificationSettings from "@/pages/settings/NotificationSettings";
import DeliveryLog from "@/pages/settings/DeliveryLog";
import Account from "@/pages/Account";
import PricingPlans from "@/pages/finance/PricingPlans";
// Phase UI-2: AutoBilling page removed from the tenant surface.
import Support from "@/pages/Support";
import ModuleDisabled from "@/pages/ModuleDisabled";
import Trash from "@/pages/Trash";
import CRM from "@/pages/CRM";
import CRMSettings from "@/pages/settings/CRMSettings";
import KGDashboard from "@/pages/kg/KGDashboard";
import KGIncidents from "@/pages/kg/KGIncidents";
import KGChildCard from "@/pages/kg/KGChildCard";
import PortalLayout from "@/pages/portal/PortalLayout";
import PortalHome from "@/pages/portal/PortalHome";
import Receipt from "@/pages/portal/Receipt";
import Certificates from "@/pages/academics/Certificates";
import ImportPeople from "@/pages/tools/ImportPeople";
import Verify from "@/pages/public/Verify";
import PlatformAdmin from "@/pages/platform/PlatformAdmin";
import SubscriptionTab from "@/pages/settings/SubscriptionTab";
import Employees from "@/pages/hr/Employees";
import EmployeeDetail from "@/pages/hr/EmployeeDetail";
import PayrollRuns from "@/pages/hr/PayrollRuns";
import PayrollRunDetail from "@/pages/hr/PayrollRunDetail";
import PayrollSummary from "@/pages/hr/PayrollSummary";
import PayrollEmployeeDetail from "@/pages/hr/PayrollEmployeeDetail";
import Tasks from "@/pages/Tasks";
import Exams from "@/pages/Exams";
import ExamDetail from "@/pages/ExamDetail";
// Phase UI-2: Security/Developer/Privacy/Integrations pages are no longer
// wired to any tenant route. The page components stay on disk (they are
// referenced by backend/tests and may return under a future phase) but they
// are unreachable from the running UI.
import Vendors from "@/pages/procurement/Vendors";
import PurchaseRequests from "@/pages/procurement/PurchaseRequests";
import PurchaseRequestDetail from "@/pages/procurement/PurchaseRequestDetail";
import InventoryItems from "@/pages/inventory/Items";
import InventoryMovements from "@/pages/inventory/Movements";
import InventoryEquipment from "@/pages/inventory/Equipment";
import ReportsCenter from "@/pages/reports/ReportsCenter";

function AppShell({ children }: { children: React.ReactNode }) {
    useTheme();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    return (
        <div className="min-h-screen flex" style={{ background: "var(--page)" }}>
            <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
            <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)} />
            <div className="flex-1 min-w-0 flex flex-col">
                <Topbar onMobileMenu={() => setMobileOpen(true)} />
                <ImpersonationBanner />
                <SubscriptionBanner />
                <PlatformTenantBanner />
                <main className="flex-1 min-w-0" style={{ padding: "24px" }} data-testid="main-content">
                    <div className="max-w-[1400px] mx-auto">{children}</div>
                </main>
            </div>
        </div>
    );
}

const Protected = ({ el, tenantScoped = true }: { el: React.ReactNode; tenantScoped?: boolean }) => (
    <ProtectedRoute><AppShell>{tenantScoped ? <RequireTenant>{el}</RequireTenant> : el}</AppShell></ProtectedRoute>
);

export default function App() {
    return (
        <BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
            <AuthProvider>
                <Routes>
                    <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
                    <Route path="/register" element={<PublicOnlyRoute><Register /></PublicOnlyRoute>} />
                    <Route path="/activate" element={<Activate />} />
                    <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/invite/accept" element={<AcceptInvite />} />
                    <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                    <Route path="/" element={<Landing />} />
                    <Route path="/dashboard" element={<Protected el={<Dashboard />} />} />
                    <Route path="/people" element={<Protected el={<People />} />} />
                    <Route path="/people/:id" element={<Protected el={<PersonDetail />} />} />
                    <Route path="/programs" element={<Protected el={<Programs />} />} />
                    <Route path="/groups" element={<Protected el={<Groups />} />} />
                    <Route path="/groups/:id" element={<Protected el={<GroupDetail />} />} />
                    <Route path="/schedule" element={<Protected el={<Schedule />} />} />
                    <Route path="/crm" element={<Protected el={<CRM />} />} />
                    <Route path="/kindergarten" element={<Protected el={<KGDashboard />} />} />
                    <Route path="/kindergarten/incidents" element={<Protected el={<KGIncidents />} />} />
                    <Route path="/kindergarten/child/:id" element={<Protected el={<KGChildCard />} />} />
                    <Route path="/finance/invoices" element={<Protected el={<Invoices />} />} />
                    <Route path="/finance/payments" element={<Protected el={<Payments />} />} />
                    <Route path="/finance/debt" element={<Protected el={<DebtPage />} />} />
                    <Route path="/finance/cash" element={<Protected el={<Cash />} />} />
                    <Route path="/finance/other-income" element={<Protected el={<OtherIncome />} />} />
                    <Route path="/finance/pricing-plans" element={<Protected el={<PricingPlans />} />} />
                    <Route path="/support" element={<Protected el={<Support />} />} />
                    <Route path="/module-disabled/:module" element={<Protected el={<ModuleDisabled />} />} />
                    <Route path="/module-disabled" element={<Protected el={<ModuleDisabled />} />} />
                    <Route path="/trash" element={<Protected el={<Trash />} />} />
                    <Route path="/settings/rooms" element={<Protected el={<Rooms />} />} />
                    <Route path="/settings/branches" element={<Protected el={<Branches />} />} />
                    <Route path="/settings/roles" element={<Protected el={<Roles />} />} />
                    <Route path="/settings/staff" element={<Protected el={<Staff />} />} />
                    <Route path="/settings/institution" element={<Protected el={<Institution />} />} />
                    <Route path="/settings/crm" element={<Protected el={<CRMSettings />} />} />
                    <Route path="/settings/audit" element={<Protected el={<AuditViewer />} />} />
                    <Route path="/settings/notifications" element={<Protected el={<NotificationSettings />} />} />
                    <Route path="/settings/notification-log" element={<Protected tenantScoped={false} el={<DeliveryLog />} />} />
                    <Route path="/account" element={<Protected tenantScoped={false} el={<Account />} />} />
                    <Route path="/portal" element={<PortalRoute><PortalLayout /></PortalRoute>}>
                        <Route index element={<PortalHome />} />
                    </Route>
                    <Route path="/portal/receipt/:id" element={<PortalRoute><Receipt /></PortalRoute>} />
                    <Route path="/academics/certificates" element={<Protected el={<Certificates />} />} />
                    <Route path="/tools/import-people" element={<Protected el={<ImportPeople />} />} />
                    <Route path="/verify/:certNumber" element={<Verify />} />
                    <Route path="/platform" element={<Protected el={<PlatformAdmin />} tenantScoped={false} />} />
                    <Route path="/settings/subscription" element={<Protected el={<SubscriptionTab />} />} />
                    <Route path="/hr" element={<Protected el={<Employees />} />} />
                    <Route path="/hr/employees" element={<Protected el={<Employees />} />} />
                    <Route path="/hr/payroll" element={<Protected el={<PayrollSummary />} />} />
                    <Route path="/hr/payroll/runs" element={<Protected el={<PayrollRuns />} />} />
                    <Route path="/hr/payroll/runs/:id" element={<Protected el={<PayrollRunDetail />} />} />
                    <Route path="/hr/payroll/:eid" element={<Protected el={<PayrollEmployeeDetail />} />} />
                    <Route path="/hr/employees/:id" element={<Protected el={<EmployeeDetail />} />} />
                    <Route path="/hr/:id" element={<Protected el={<EmployeeDetail />} />} />
                    <Route path="/tasks" element={<Protected el={<Tasks />} />} />
                    <Route path="/exams" element={<Protected el={<Exams />} />} />
                    <Route path="/exams/:id" element={<Protected el={<ExamDetail />} />} />
                    <Route path="/procurement" element={<Protected el={<PurchaseRequests />} />} />
                    <Route path="/procurement/requests" element={<Protected el={<PurchaseRequests />} />} />
                    <Route path="/procurement/requests/:id" element={<Protected el={<PurchaseRequestDetail />} />} />
                    <Route path="/procurement/vendors" element={<Protected el={<Vendors />} />} />
                    <Route path="/inventory" element={<Protected el={<InventoryItems />} />} />
                    <Route path="/inventory/items" element={<Protected el={<InventoryItems />} />} />
                    <Route path="/inventory/movements" element={<Protected el={<InventoryMovements />} />} />
                    <Route path="/inventory/equipment" element={<Protected el={<InventoryEquipment />} />} />
                    <Route path="/reports" element={<Protected el={<ReportsCenter />} />} />
                    <Route path="*" element={<Protected el={<Dashboard />} />} />
                </Routes>
            </AuthProvider>
        </BrowserRouter>
    );
}